import AccountRow from '@atoms/AccountRow';
import { Divider, Text } from '@components/primitives';
import ModalHeader from '@components/primitives/ModalHeader';
import colors from '@theme/colors';
import AmplitudeHelper from '@utils/analytics';
import { AMPLITUDE_CONSTANTS } from '@utils/analytics/constants';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { FlatList, StyleProp, ViewStyle, Modal, View, TouchableOpacity } from 'react-native';
import styles from './styles';

interface OpenPeriod {
  start: string;
  end: string;
  payDateWithdrawal: boolean;
}

interface Slider {
  enabled: boolean;
  incrementBy: number;
  minValue: number;
  maxValue: number;
}

interface WithdrawalButton {
  enabled: boolean;
}

export interface WithdrawalAccount {
  id: number;
  accountTypeId: number;
  balance: number;
  displayBalance: boolean;
  icon: string;
  color: string;
  displayName: string;
  amplitudeAccountName: string;
  isDynamic: boolean;
  displayOrder: number;
  enableAccountCard: boolean;
  screenName?: string | null;
  hasSlider: boolean;
  sliderStep: number;
  buttonText: string;
  defaultSliderPercentage: number;
  minTxnValue: number;
  isWithdrawalEligible: boolean;
  isBillPaymentEligible: boolean;
  status: string;
  openPeriod?: OpenPeriod;
  slider?: Slider;
  withdrawalButton?: WithdrawalButton;
  defaultWithdrawalAmount: number;
  canShowNew: boolean;
}

interface SelectedAccount {
  id?: number;
}

interface ModalAccountsProps {
  withdrawlAccounts?: WithdrawalAccount[];
  showAccountSelector?: boolean;
  setSelectedAccount?: (account: WithdrawalAccount) => void;
  setShowAccountSelector?: (show: boolean) => void;
  source?: string;
  selectedAccount?: SelectedAccount;
  excludeNewTag?: boolean;
}

// Component

function getItemSeperatorComponent() {
  return () => <Divider />;
}

const ModalAccounts: React.FC<ModalAccountsProps> = ({
  withdrawlAccounts = [],
  showAccountSelector = false,
  setSelectedAccount = () => {},
  setShowAccountSelector = () => {},
  source = '',
  selectedAccount = { id: 0 },
  excludeNewTag = false,
}) => {
  const { t } = useTranslation('common');
  // Remove the ref since we're controlling the BottomSheet with the index prop

  const closeAccountSelector = () => {
    const e = AMPLITUDE_CONSTANTS.Withdrawal.CancelButtonClicked;
    const { name, attributes } = e;
    AmplitudeHelper.logEvent(name, {
      [attributes.source]: source,
    });
    setShowAccountSelector(false);
  };

  // Remove the useEffect that calls snapToIndex and close
  // The BottomSheet will be controlled by the index prop instead

  return (
    <Modal
      visible={showAccountSelector}
      animationType="none"
      presentationStyle="overFullScreen"
      transparent
      onRequestClose={closeAccountSelector}>
      <TouchableOpacity style={styles.modalOverlay} activeOpacity={1} onPress={closeAccountSelector}>
        <TouchableOpacity style={styles.modalContent} activeOpacity={1} onPress={e => e.stopPropagation()}>
          <ModalHeader title={t('withdrawlSlider.selectData')} onClose={closeAccountSelector} />
          {withdrawlAccounts.length > 0 ? (
            <FlatList
              testID="fltLstAccounts"
              data={withdrawlAccounts}
              style={styles.accountListStyle as StyleProp<ViewStyle>}
              scrollEnabled={withdrawlAccounts.length > 5} // Only enable scroll if there are many items
              contentContainerStyle={[
                styles.modalContainer,
                styles.flatListContent, // Allow content to grow
              ]}
              ItemSeparatorComponent={getItemSeperatorComponent()}
              keyExtractor={item => item.id.toString()}
              renderItem={({ item }) => (
                <AccountRow
                  setShowAccountSelector={setShowAccountSelector}
                  setSelectedAccount={setSelectedAccount}
                  item={item}
                  isSelected={selectedAccount?.id === item.id}
                  excludeNewTag={excludeNewTag}
                />
              )}
            />
          ) : (
            <View style={styles.emptyStateContainer}>
              <Text variant="md-normal" color={colors.neutral.darkMist} textAlign="center">
                {t('common.suspendedError')}
              </Text>
            </View>
          )}
        </TouchableOpacity>
      </TouchableOpacity>
    </Modal>
  );
};

ModalAccounts.defaultProps = {
  withdrawlAccounts: [],
  showAccountSelector: false,
  setSelectedAccount: () => {},
  setShowAccountSelector: () => {},
  source: '',
  selectedAccount: {},
  excludeNewTag: false,
};
export default ModalAccounts;
